import { useRef, useState } from 'react';
import { parseSpecSheet } from './parseSpecSheet';
import type { ParsedSpec, ParseError } from './types';

interface Props {
  onParsed: (spec: ParsedSpec) => void;
}

function errorMessage(err: ParseError): string {
  switch (err.kind) {
    case 'not_xlsx':
      return 'Файл не является xlsx. Загрузите файл Excel (.xlsx).';
    case 'no_sheet':
      return 'Файл не содержит листов.';

    case 'invalid_header':
      return 'Структура таблицы не соответствует ожидаемой. Проверьте, что столбец A — «№», столбец T — «К-ВО».';
    case 'empty':
      return 'Таблица пуста или не содержит данных.';
  }
}

export function SpecUpload({ onParsed }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function handleFile(file: File) {
    if (!file.name.endsWith('.xlsx')) {
      setError(errorMessage({ kind: 'not_xlsx' }));
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const buffer = await file.arrayBuffer();
      const result = parseSpecSheet(buffer);
      if (result.ok) {
        onParsed(result.data);
      } else {
        setError(errorMessage(result.error));
      }
    } catch (e) {
      setError('Не удалось прочитать файл: ' + (e instanceof Error ? e.message : String(e)));
    } finally {
      setLoading(false);
    }
  }

  function onInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    e.target.value = '';
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <h2 className="text-base font-semibold text-gray-800 mb-4">Спецификация SolidWorks</h2>

      <div
        className={[
          'border-2 border-dashed rounded-lg p-10 text-center cursor-pointer transition-colors',
          dragging
            ? 'border-blue-400 bg-blue-50'
            : 'border-gray-300 bg-gray-50 hover:border-gray-400 hover:bg-gray-100',
          loading ? 'pointer-events-none opacity-60' : '',
        ].join(' ')}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && inputRef.current?.click()}
        aria-label="Загрузить xlsx-файл спецификации"
      >
        <input
          ref={inputRef}
          type="file"
          accept=".xlsx"
          className="hidden"
          onChange={onInputChange}
        />

        {loading ? (
          <p className="text-sm text-gray-500">Разбор файла…</p>
        ) : (
          <>
            <p className="text-sm text-gray-600">
              Перетащите xlsx-файл или{' '}
              <span className="text-blue-600 underline">выберите на диске</span>
            </p>
            <p className="text-xs text-gray-400 mt-1">
              Ожидается спецификация SolidWorks: лист «Лист1», столбцы A–T
            </p>
          </>
        )}
      </div>

      {error && (
        <div className="mt-3 rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}
    </div>
  );
}
